import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  IconButton,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";

import Nodatafound from "components/nodatafound/Nodatafound";
import {
  speedLearnTopicBySubjectIdAsync,
  speedLearnByTopicIdAsync,
} from "redux/speedlearn/speedlearn.async";
import CourseCard from "./CourseCard";

function SpeedLearn() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { subjectIdNew } = location?.state || {};

  const { getOnlySiteSettingData = [] } = useSelector(
    (state) => state.getOnlySiteSetting
  );
  const { siteTitle } = getOnlySiteSettingData;

  const { subjectBy } = useSelector((state) => state?.subject);
  const {
    speedlearntopicsLoader,
    speedlearntopics,
    speedLearnVideoLoader,
    speedLearnVideo,
  } = useSelector((state) => state?.speedLearn);

  const [subjectFilter, setSubjectFilter] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const [dotStartIndex, setDotStartIndex] = useState(0);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const maxVisibleDots = isMobile ? 6 : 10;

  const SUBJECT_COLORS = [
    "#3B82F6",
    "#EF4444",
    "#F59E0B",
    "#10B981",
    "#8B5CF6",
  ];

  useEffect(() => {
    if (subjectBy?.length && !subjectFilter) {
      setSubjectFilter(subjectIdNew || subjectBy[0]?.id);
    }
  }, [subjectBy, subjectFilter, subjectIdNew]);

  useEffect(() => {
    if (subjectFilter) {
      dispatch(speedLearnTopicBySubjectIdAsync({ subjectId: subjectFilter }));
      setSelectedIndex(0);
      setDotStartIndex(0);
    }
  }, [subjectFilter, dispatch]);

  const getSubjectColor = (id) => SUBJECT_COLORS[id % SUBJECT_COLORS.length];

  const handleDotClick = (index) => {
    if (index !== selectedIndex) {
      setDirection(index > selectedIndex ? 1 : -1);
      setSelectedIndex(index);

      if (index < dotStartIndex) {
        setDotStartIndex(index);
      } else if (index >= dotStartIndex + maxVisibleDots) {
        setDotStartIndex(index - maxVisibleDots + 1);
      }
    }
  };

  const handlePrev = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setDirection(-1);
      setSelectedIndex(newIndex);

      if (newIndex < dotStartIndex) {
        setDotStartIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const handleNext = () => {
    if (selectedIndex < speedlearntopics.length - 1) {
      const newIndex = selectedIndex + 1;
      setDirection(1);
      setSelectedIndex(newIndex);

      if (newIndex >= dotStartIndex + maxVisibleDots) {
        setDotStartIndex((prev) => prev + 1);
      }
    }
  };

  const currentTopic = speedlearntopics?.[selectedIndex];

  useEffect(() => {
    if (currentTopic?.id) {
      dispatch(speedLearnByTopicIdAsync({ topicId: currentTopic.id }));
    }
  }, [currentTopic, dispatch]);

  const visibleDots = speedlearntopics?.slice(
    dotStartIndex,
    dotStartIndex + maxVisibleDots
  );

  const handlePlayClick = (item) => {
    navigate(`/app/speed-learn/play/${item.id}`, {
      state: {
        videoData: item,
        subjectIdRedirect: subjectFilter,
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>
          {typeof siteTitle === "string"
            ? `Speed Learn | ${siteTitle}`
            : "Speed Learn" | "Veda Academy"}
        </title>
      </Helmet>

      {/* Header */}
      <Box sx={{ mt: 2, ml: 2 }}>
        <ArrowBackIcon
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
      </Box>

      <Container>
        {/* SUBJECT TABS */}
        <Tabs
          value={subjectFilter || false}
          onChange={(e, v) => setSubjectFilter(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ mb: 4 }}
        >
          {subjectBy?.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={<Typography fontWeight={600}>{item.name}</Typography>}
            />
          ))}
        </Tabs>

        {speedlearntopicsLoader ? (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        ) : speedlearntopics?.length ? (
          <>
            {/* TOPIC ANIMATION */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTopic?.id}
                initial={{ opacity: 0, y: direction > 0 ? 20 : -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: direction > 0 ? -20 : 20 }}
                transition={{ duration: 0.3 }}
              >
                <Typography
                  textAlign="center"
                  fontSize={{ xs: 18, sm: 22, md: 24 }}
                  fontWeight={700}
                  color={getSubjectColor(subjectFilter)}
                  mb={2}
                >
                  {currentTopic?.topic}
                </Typography>
              </motion.div>
            </AnimatePresence>

            {/* DOTS + ARROWS */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 3,
              }}
            >
              <IconButton
                onClick={handlePrev}
                disabled={selectedIndex === 0}
                sx={{ opacity: selectedIndex === 0 ? 0.4 : 1 }}
              >
                <ChevronLeftIcon />
              </IconButton>

              {visibleDots.map((_, idx) => {
                const actualIndex = dotStartIndex + idx;
                return (
                  <Box
                    key={actualIndex}
                    onClick={() => handleDotClick(actualIndex)}
                    sx={{
                      width: selectedIndex === actualIndex ? 14 : 10,
                      height: selectedIndex === actualIndex ? 14 : 10,
                      borderRadius: "50%",
                      bgcolor:
                        selectedIndex === actualIndex
                          ? getSubjectColor(subjectFilter)
                          : "#CBD5E1",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": { transform: "scale(1.2)" },
                    }}
                  />
                );
              })}

              <IconButton
                onClick={handleNext}
                disabled={selectedIndex === speedlearntopics.length - 1}
                sx={{
                  opacity:
                    selectedIndex === speedlearntopics.length - 1 ? 0.4 : 1,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* VIDEOS */}
            {speedLearnVideoLoader ? (
              <Box textAlign="center" py={6}>
                <CircularProgress />
              </Box>
            ) : speedLearnVideo?.length ? (
              <Stack spacing={2}>
                {speedLearnVideo.map((item) => (
                  <CourseCard
                    key={item.id}
                    item={item}
                    onPlay={handlePlayClick}
                  />
                ))}
              </Stack>
            ) : (
              <Nodatafound text="No Record Found!" />
            )}
          </>
        ) : (
          <Nodatafound text="No Record Found!" />
        )}
      </Container>
    </>
  );
}

export default SpeedLearn;
